import { Interval } from 'luxon';
import React, { useState } from 'react';
import styled from 'styled-components';

import { MOBILE_BREAKPOINT } from 'common/hooks/useIsMobile';
import { InfiniteTable } from 'modules/experience-core';
import { InsightEntity } from 'modules/insights-v2/types';

import ErrorPage from '../components/ErrorPage';
import LoadingPage from '../components/LoadingPage';
import { OverallPerformance } from '../components/OverallPerformance';
import { DeliveryTypesChart } from '../components/performance/DeliveryTypesChart';
import { OrderTypeTotalsInsights } from '../components/performance/OrderTypeTotalsInsights';
import { PerformanceByVenueCard } from '../components/performance/PerformanceByVenueCard';
import PerformanceByVenueModal from '../components/performance/PerformanceByVenueModal';
import { VenueTableSortableColumnIDs } from '../components/tables/performance/PerformanceByVenueTable';
import { useDeliveryInsight, usePerformanceInsightV2WithPreviousInterval } from '../hooks/useInsightApi';
import usePerformanceByVenueInsights from '../hooks/usePerformanceByVenueInsights';
import { useTelemetryBusinessInsightsV2PerformanceView } from '../telemetry';
import { isAllError, isInitialLoading } from '../utils';

type Props = {
  insightEntity: InsightEntity;
  dateInterval: Interval;
  isVATEnabled: boolean;
};

const PerformanceByVenueRoot = styled.div`
  margin: 24px;
`;

const PerformanceInsightsRoot = styled.div`
  align-items: flex-start;
  display: grid;
  gap: 24px;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr);
  margin: 24px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const PerformancePage = ({ insightEntity, dateInterval, isVATEnabled }: Props) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  useTelemetryBusinessInsightsV2PerformanceView(insightEntity.type);

  const performanceInsightV2 = usePerformanceInsightV2WithPreviousInterval({
    insightEntity,
    dateInterval,
  });

  const deliveryInsight = useDeliveryInsight(insightEntity, dateInterval);

  const ROWS_LIMIT = 5;
  const [sort, setSort] = useState<InfiniteTable.SortState<VenueTableSortableColumnIDs> | undefined>(undefined);
  const performanceByVenueInsights = usePerformanceByVenueInsights({
    entityType: insightEntity.type,
    entityId: insightEntity.id,
    interval: dateInterval,
    sortBy: sort?.sortBy,
    sortOrder: sort?.sortOrder,
    limit: ROWS_LIMIT,
  });

  const allInsightResponses = [performanceInsightV2, performanceByVenueInsights, deliveryInsight];

  if (isInitialLoading(allInsightResponses)) {
    return <LoadingPage />;
  }
  if (isAllError(allInsightResponses)) {
    return (
      <ErrorPage
        onRetry={() => {
          allInsightResponses.forEach(insight => insight.refetch());
        }}
      />
    );
  }

  return (
    <>
      <OverallPerformance
        isVATEnabled={isVATEnabled}
        performanceInsight={performanceInsightV2}
        isWoltPlusEnabled={!!insightEntity.isWoltPlus}
        isHeaderVisible={false}
      />
      <PerformanceInsightsRoot>
        <DeliveryTypesChart deliveryInsight={deliveryInsight} />
        {!!insightEntity.isWoltPlus && (
          <OrderTypeTotalsInsights performanceInsight={performanceInsightV2} isVATEnabled={isVATEnabled} />
        )}
      </PerformanceInsightsRoot>
      {insightEntity.type === 'venue' ? null : (
        <PerformanceByVenueRoot>
          <PerformanceByVenueCard
            isVATEnabled={isVATEnabled}
            performanceByVenueInsights={performanceByVenueInsights}
            sort={sort}
            setSort={setSort}
            onShowAll={() => setIsModalOpen(true)}
          />
          {isModalOpen && (
            <PerformanceByVenueModal
              isVATEnabled={isVATEnabled}
              dateInterval={dateInterval}
              insightEntity={insightEntity}
              isOpen
              onClose={() => setIsModalOpen(false)}
            />
          )}
        </PerformanceByVenueRoot>
      )}
    </>
  );
};
export default PerformancePage;
